import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LogoutIcon from '@mui/icons-material/Logout'
import useAuthStore from '../store/authStore'
import { brandColors } from '../brandColors'
import Sidebar from './Sidebar'
import DashboardPage from '../pages/DashboardPage'
import UndeliveredQuantityPage from '../pages/UndeliveredQuantityPage'
import ProductUploadPage from '../pages/ProductUploadPage'
import PurchasableStatusUploadPage from '../pages/PurchasableStatusUploadPage'
import ProductManagementPage from '../pages/ProductManagementPage'
import ProductDownloadsPage from '../pages/ProductDownloadsPage'
import ProductDetailPage from '../pages/ProductDetailPage'
import BulkBarcodeUploadPage from '../pages/BulkBarcodeUploadPage'
import BulkProductEditPage from '../pages/BulkProductEditPage'
import InventoryUploadPage from '../pages/InventoryUploadPage'
import InventoryManagementPage from '../pages/InventoryManagementPage'
import InventoryDetailPage from '../pages/InventoryDetailPage'
import ReportsPage from '../pages/ReportsPage'
import OrdersListPage from '../pages/OrdersListPage'
import PurchaseOrderExportsPage from '../pages/PurchaseOrderExportsPage'
import CreatePurchaseOrderPage from '../pages/CreatePurchaseOrderPage'
import PurchaseOrderCsvUploadPage from '../pages/PurchaseOrderCsvUploadPage'
import PurchaseOrderImportSessionPage from '../pages/PurchaseOrderImportSessionPage'
import PurchaseOrderImportReviewPage from '../pages/PurchaseOrderImportReviewPage'
import OrderDetailPage from '../pages/OrderDetailPage'
import PaymentProcessingListPage from '../pages/PaymentProcessingListPage'
import RejectedPaymentProofsPage from '../pages/RejectedPaymentProofsPage'
import AdminDepositsListPage from '../pages/AdminDepositsListPage'
import AdminDepositDetailPage from '../pages/AdminDepositDetailPage'
import InventorySyncApprovalsPage from '../pages/InventorySyncApprovalsPage'
import InventorySyncDetailPage from '../pages/InventorySyncDetailPage'
import InventorySyncCreatePage from '../pages/InventorySyncCreatePage'
import PaymentSettingsPage from '../pages/PaymentSettingsPage'
import CreditNoteSettingsPage from '../pages/CreditNoteSettingsPage'
import PriceConcernsPage from '../pages/PriceConcernsPage'
import LowStockSettingsPage from '../pages/LowStockSettingsPage'
import PromotionsUploadPage from '../pages/PromotionsUploadPage'
import PromotionsManagePage from '../pages/PromotionsManagePage'
import LocationUploadPage from '../pages/LocationUploadPage'
import LocationManagementPage from '../pages/LocationManagementPage'
import StoreLocationMappingPage from '../pages/StoreLocationMappingPage'
import StoreLocationMappingOrgDetailPage from '../pages/StoreLocationMappingOrgDetailPage'
import CategoryUploadPage from '../pages/CategoryUploadPage'
import CategoryManagementPage from '../pages/CategoryManagementPage'
import DivisionUploadPage from '../pages/DivisionUploadPage'
import DivisionManagementPage from '../pages/DivisionManagementPage'
import DeliveryTimeUploadPage from '../pages/DeliveryTimeUploadPage'
import DeliveryTimeManagementPage from '../pages/DeliveryTimeManagementPage'
import UserPermissionsPage from '../pages/UserPermissionsPage'
import BulkRoleAssignmentPage from '../pages/BulkRoleAssignmentPage'
import BulkRoleCreationPage from '../pages/BulkRoleCreationPage'
import AdminPasswordsPage from '../pages/AdminPasswordsPage'
import CriticalAuditPage from '../pages/CriticalAuditPage'
import ChangePasswordPage from '../pages/ChangePasswordPage'
import StockSyncAdminPage from '../pages/StockSyncAdminPage'
import PromotionSyncAdminPage from '../pages/PromotionSyncAdminPage'
import PurchasableStatusAdminPage from '../pages/PurchasableStatusAdminPage'
import CustomerSupportPage from '../pages/CustomerSupportPage'
import DefaultOrgLocationSettingsPage from '../pages/DefaultOrgLocationSettingsPage'
import SalesPolicyPage from '../pages/SalesPolicyPage'
import VendorManagementPage from '../pages/VendorManagementPage'
import LoginPageContentPage from '../pages/LoginPageContentPage'
import NonInventorySalesReportPage from '../pages/NonInventorySalesReportPage'
import NegativeSalesExportsPage from '../pages/NegativeSalesExportsPage'
import ShopsPage from '../pages/ShopsPage'
import ShopExportsPage from '../pages/ShopExportsPage'
import SalesCompiledExportsPage from '../pages/SalesCompiledExportsPage'
import ModifyUserInventoryPage from '../pages/ModifyUserInventoryPage'
import ReleasesListPage from '../pages/ReleasesListPage'
import ReleaseCreatePage from '../pages/ReleaseCreatePage'
import ReleaseDetailPage from '../pages/ReleaseDetailPage'
import PosStorePromotionsListPage from '../pages/PosStorePromotionsListPage'
import PosStorePromotionsReportPage from '../pages/PosStorePromotionsReportPage'
import PosStorePromotionsUploadPage from '../pages/PosStorePromotionsUploadPage'
import ManageUpcomingPromotionsPage from '../pages/ManageUpcomingPromotionsPage'
import UpcomingPromotionExportsPage from '../pages/UpcomingPromotionExportsPage'
import PosSaleInvoiceDetailPage from '../pages/PosSaleInvoiceDetailPage'
import { authService } from '../services'
import {
  isPromotionsOnlyActor as checkPromotionsOnly,
  isCustomerSupportOnlyActor as checkCustomerSupportOnly,
  isCustomerSupportOnlyAllowedPath,
  hasAdminPermission,
  canExportData,
  canReadSidebarPath,
  PERMISSION_SUBJECTS,
} from '../utils/adminPermissions'

const CUSTOMER_SUPPORT_HOME = '/administration/customer-support'

/** Gate module routes (including write/upload paths) via canReadSidebarPath. */
const RequireSidebarPath = ({ path, children }) => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const promotionsOnly = checkPromotionsOnly(isSuperuserActor, adminPermissions)
  const customerSupportOnly = checkCustomerSupportOnly(
    isSuperuserActor,
    adminPermissions,
    adminRoleCodes,
  )
  if (promotionsOnly) {
    return <Navigate to="/promotions/manage" replace />
  }
  if (!canReadSidebarPath(adminPermissions, path)) {
    return (
      <Navigate
        to={customerSupportOnly ? CUSTOMER_SUPPORT_HOME : '/dashboard'}
        replace
      />
    )
  }
  return children
}

const RequireAdministrationRead = ({ subjects, children }) => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const promotionsOnly = checkPromotionsOnly(isSuperuserActor, adminPermissions)
  const customerSupportOnly = checkCustomerSupportOnly(
    isSuperuserActor,
    adminPermissions,
    adminRoleCodes,
  )
  if (promotionsOnly) {
    return <Navigate to="/promotions/manage" replace />
  }
  const allowed = (subjects || []).some((subject) =>
    hasAdminPermission(adminPermissions, subject, 'read'),
  )
  if (!allowed) {
    return (
      <Navigate
        to={customerSupportOnly ? CUSTOMER_SUPPORT_HOME : '/dashboard'}
        replace
      />
    )
  }
  return children
}

/** Keep Senior/Junior CS on Customer Support (and change-password). */
const CustomerSupportScopeGate = ({ children }) => {
  const location = useLocation()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const customerSupportOnly = checkCustomerSupportOnly(
    isSuperuserActor,
    adminPermissions,
    adminRoleCodes,
  )
  if (
    customerSupportOnly &&
    !isCustomerSupportOnlyAllowedPath(location.pathname)
  ) {
    return <Navigate to={CUSTOMER_SUPPORT_HOME} replace />
  }
  return children
}

const DRAWER_WIDTH = 280
const DRAWER_COLLAPSED_WIDTH = 80

const DashboardLayout = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [mobileOpen, setMobileOpen] = useState(false)
  const [drawerCollapsed, setDrawerCollapsed] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const token = useAuthStore((state) => state.token)
  const user = useAuthStore((state) => state.user)
  const isSuperuserActor = useAuthStore((state) => state.isSuperuserActor)
  const adminPermissions = useAuthStore((state) => state.adminPermissions)
  const logout = useAuthStore((state) => state.logout)
  const setSessionFromVerify = useAuthStore((state) => state.setSessionFromVerify)

  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const isPromotionsOnly = checkPromotionsOnly(isSuperuserActor, adminPermissions)
  const isCustomerSupportOnly = checkCustomerSupportOnly(
    isSuperuserActor,
    adminPermissions,
    adminRoleCodes,
  )
  const canCreateOrder = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ORDER,
    'create',
  )
  const canExportOrders = canExportData(adminPermissions)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      try {
        const data = await authService.verifyAdmin()
        if (!cancelled) setSessionFromVerify(data)
      } catch {
        /* session may be invalid; 401 handled by apiClient */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, setSessionFromVerify])

  const drawerWidth = drawerCollapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleDrawerCollapse = () => {
    setDrawerCollapsed(!drawerCollapsed)
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: brandColors.gradient,
          boxShadow: '0 2px 8px rgba(46, 125, 50, 0.15)',
          transition: 'margin-left 0.3s ease',
          marginLeft: isMobile ? 0 : drawerWidth,
          width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Shwapno Admin
          </Typography>

          {/* User Menu */}
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              transition: 'all 0.2s ease',
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'rgba(255,255,255,0.3)',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0) || 'A'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff' }}>
                {user?.name?.split(' ')[0] || 'Admin'}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose()
                navigate('/account/change-password')
              }}
            >
              Change password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              mt: 8,
              bgcolor: '#ffffff',
              borderRight: '1px solid #e0e6ed',
            },
          }}
        >
          <Sidebar collapsed={false} onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      )}

      {/* Desktop Drawer - Collapsible */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            transition: 'width 0.3s ease',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 0,
              height: '100vh',
              bgcolor: '#ffffff',
              borderRight: '1px solid #e0e6ed',
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
        >
          <Sidebar
            collapsed={drawerCollapsed}
            showCollapseToggle
            onToggleCollapse={handleDrawerCollapse}
          />
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          mt: 8,
          transition: 'all 0.3s ease',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            maxWidth: '1400px',
            width: '100%',
            minWidth: 0,
            margin: '0 auto',
            animation: 'fadeIn 0.3s ease',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <CustomerSupportScopeGate>
          <Routes>
            <Route
              path="/dashboard"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : isCustomerSupportOnly ? (
                  <Navigate to={CUSTOMER_SUPPORT_HOME} replace />
                ) : (
                  <DashboardPage />
                )
              }
            />
            <Route
              path="/dashboard/undelivered-quantity"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : (
                  <UndeliveredQuantityPage />
                )
              }
            />
            <Route
              path="/products/upload"
              element={
                <RequireSidebarPath path="/products/upload">
                  <ProductUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/products/purchasable-status"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <PurchasableStatusUploadPage />}
            />
            <Route
              path="/products/barcodes/upload"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <BulkBarcodeUploadPage />}
            />
            <Route
              path="/products/bulk-edit"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <BulkProductEditPage />}
            />
            <Route
              path="/products/manage"
              element={
                <RequireSidebarPath path="/products/manage">
                  <ProductManagementPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/products/downloads"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <ProductDownloadsPage />}
            />
            <Route
              path="/products/:id"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <ProductDetailPage />}
            />
            <Route
              path="/inventory/upload"
              element={
                <RequireSidebarPath path="/inventory/upload">
                  <InventoryUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/inventory/manage"
              element={
                <RequireSidebarPath path="/inventory/manage">
                  <InventoryManagementPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/inventory/modify-user"
              element={
                <RequireSidebarPath path="/inventory/modify-user">
                  <ModifyUserInventoryPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/inventory/:id"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <InventoryDetailPage />}
            />
            <Route
              path="/orders/purchase"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <OrdersListPage />}
            />
            <Route
              path="/orders/purchase/payment-processing"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : (
                  <PaymentProcessingListPage />
                )
              }
            />
            <Route
              path="/orders/purchase/deposits"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : (
                  <AdminDepositsListPage />
                )
              }
            />
            <Route
              path="/orders/purchase/deposits/:requestId"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : (
                  <AdminDepositDetailPage />
                )
              }
            />
            <Route
              path="/orders/purchase/rejected-payment-proofs"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : (
                  <RejectedPaymentProofsPage />
                )
              }
            />
            <Route
              path="/orders/purchase/exports"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : canExportOrders ? (
                  <PurchaseOrderExportsPage />
                ) : (
                  <Navigate
                    to="/orders/purchase"
                    replace
                    state={{
                      permissionDenied:
                        'You need Excel / CSV export permission to view purchase-order exports.',
                    }}
                  />
                )
              }
            />
            <Route
              path="/orders/purchase/create"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : canCreateOrder ? (
                  <CreatePurchaseOrderPage />
                ) : (
                  <Navigate
                    to="/orders/purchase"
                    replace
                    state={{
                      permissionDenied:
                        'This action requires Create order access.',
                    }}
                  />
                )
              }
            />
            <Route
              path="/orders/purchase/create/upload/:sessionId/review"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : canCreateOrder ? (
                  <PurchaseOrderImportReviewPage />
                ) : (
                  <Navigate to="/orders/purchase" replace />
                )
              }
            />
            <Route
              path="/orders/purchase/create/upload/:sessionId"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : canCreateOrder ? (
                  <PurchaseOrderImportSessionPage />
                ) : (
                  <Navigate to="/orders/purchase" replace />
                )
              }
            />
            <Route
              path="/orders/purchase/create/upload"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : canCreateOrder ? (
                  <PurchaseOrderCsvUploadPage />
                ) : (
                  <Navigate to="/orders/purchase" replace />
                )
              }
            />
            <Route
              path="/orders/sales"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <OrdersListPage />}
            />
            <Route
              path="/orders/:id"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <OrderDetailPage />}
            />
            <Route
              path="/orders"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <Navigate to="/orders/purchase" replace />}
            />
            <Route
              path="/inventory-sync/create"
              element={
                <RequireSidebarPath path="/inventory-sync/create">
                  <InventorySyncCreatePage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/inventory-sync"
              element={
                <RequireSidebarPath path="/inventory-sync">
                  <InventorySyncApprovalsPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/inventory-sync/:syncId"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <InventorySyncDetailPage />}
            />
            <Route
              path="/promotions/upload"
              element={
                <RequireSidebarPath path="/promotions/upload">
                  <PromotionsUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/promotions/manage"
              element={
                <RequireSidebarPath path="/promotions/manage">
                  <PromotionsManagePage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/locations/upload"
              element={
                <RequireSidebarPath path="/locations/upload">
                  <LocationUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/locations/manage"
              element={
                <RequireSidebarPath path="/locations/manage">
                  <LocationManagementPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/store-location-mapping"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <StoreLocationMappingPage />}
            />
            <Route
              path="/store-location-mapping/:orgId"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <StoreLocationMappingOrgDetailPage />}
            />
            <Route
              path="/categories/upload"
              element={
                <RequireSidebarPath path="/categories/upload">
                  <CategoryUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/categories/manage"
              element={
                <RequireSidebarPath path="/categories/manage">
                  <CategoryManagementPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/divisions/upload"
              element={
                <RequireSidebarPath path="/divisions/upload">
                  <DivisionUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/divisions/manage"
              element={
                <RequireSidebarPath path="/divisions/manage">
                  <DivisionManagementPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/delivery-times/upload"
              element={
                <RequireSidebarPath path="/delivery-times/upload">
                  <DeliveryTimeUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/delivery-times/manage"
              element={
                <RequireSidebarPath path="/delivery-times/manage">
                  <DeliveryTimeManagementPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/reports/sales/exports"
              element={
                <RequireSidebarPath path="/reports/sales">
                  <SalesCompiledExportsPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/reports/sales"
              element={
                <RequireSidebarPath path="/reports/sales">
                  <ReportsPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/reports/inventory-stock"
              element={
                <RequireSidebarPath path="/reports/inventory-stock">
                  <ReportsPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/reports"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <Navigate to="/reports/sales" replace />}
            />
            <Route
              path="/pos-store-promotions/invoices/:orderId"
              element={<PosSaleInvoiceDetailPage />}
            />
            <Route
              path="/pos-store-promotions/report"
              element={
                <RequireSidebarPath path="/pos-store-promotions/report">
                  <PosStorePromotionsReportPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/pos-store-promotions/upload"
              element={
                <RequireSidebarPath path="/pos-store-promotions/upload">
                  <PosStorePromotionsUploadPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/pos-store-promotions/upcoming/exports"
              element={
                <RequireSidebarPath path="/pos-store-promotions/upcoming">
                  {canExportOrders ? (
                    <UpcomingPromotionExportsPage />
                  ) : (
                    <Navigate
                      to="/pos-store-promotions/upcoming"
                      replace
                      state={{
                        permissionDenied:
                          'You need Excel / CSV export permission to view upcoming promotion exports.',
                      }}
                    />
                  )}
                </RequireSidebarPath>
              }
            />
            <Route
              path="/pos-store-promotions/upcoming"
              element={
                <RequireSidebarPath path="/pos-store-promotions/upcoming">
                  <ManageUpcomingPromotionsPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/pos-store-promotions"
              element={
                <RequireSidebarPath path="/pos-store-promotions">
                  <PosStorePromotionsListPage />
                </RequireSidebarPath>
              }
            />
            <Route
              path="/payment-settings"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.PAYMENT_SETTINGS]}>
                  <PaymentSettingsPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/global-settings/credit-note"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.CREDIT_NOTE_SETTINGS]}>
                  <CreditNoteSettingsPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/global-settings/inventory"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.INVENTORY_THRESHOLDS]}>
                  <LowStockSettingsPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/user-permissions"
              element={
                <RequireAdministrationRead
                  subjects={[
                    PERMISSION_SUBJECTS.ADMIN_WHITELIST,
                    PERMISSION_SUBJECTS.ADMIN_USER_ROLES,
                  ]}
                >
                  <UserPermissionsPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/bulk-role-assignment"
              element={
                <RequireAdministrationRead
                  subjects={[PERMISSION_SUBJECTS.BULK_ROLE_ASSIGNMENT]}
                >
                  <BulkRoleAssignmentPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/bulk-role-creation"
              element={
                <RequireAdministrationRead
                  subjects={[PERMISSION_SUBJECTS.BULK_ROLE_CREATION]}
                >
                  <BulkRoleCreationPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/admin-passwords"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.ADMIN_PASSWORDS]}>
                  <AdminPasswordsPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/critical-audit"
              element={
                <RequireAdministrationRead
                  subjects={[PERMISSION_SUBJECTS.CRITICAL_AUDIT]}
                >
                  <CriticalAuditPage />
                </RequireAdministrationRead>
              }
            />
            <Route path="/account/change-password" element={<ChangePasswordPage />} />
            <Route
              path="/vendors"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : isSuperuserActor ? (
                  <VendorManagementPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route path="/administration/vendors" element={<Navigate to="/vendors" replace />} />
            <Route
              path="/administration/stock-sync"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.STOCK_SYNC]}>
                  <StockSyncAdminPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/promotion-sync"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.STOCK_SYNC]}>
                  <PromotionSyncAdminPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/purchasable-status-sync"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.STOCK_SYNC]}>
                  <PurchasableStatusAdminPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/customer-support"
              element={
                <RequireAdministrationRead
                  subjects={[PERMISSION_SUBJECTS.CUSTOMER_SUPPORT]}
                >
                  <CustomerSupportPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/default-org-location"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.DEFAULT_ORG_LOCATION]}>
                  <DefaultOrgLocationSettingsPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/administration/sales-policy"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : isSuperuserActor ? (
                  <SalesPolicyPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/administration/negative-sales"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : isSuperuserActor ? (
                  <NonInventorySalesReportPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/administration/negative-sales/exports"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : isSuperuserActor ? (
                  <NegativeSalesExportsPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/content-management/login"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : isSuperuserActor ? (
                  <LoginPageContentPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/shops/exports"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : (
                  <ShopExportsPage />
                )
              }
            />
            <Route
              path="/shops"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <ShopsPage />}
            />
            <Route
              path="/credit-notes"
              element={
                <Navigate to={isPromotionsOnly ? '/promotions/manage' : '/dashboard'} replace />
              }
            />
            <Route
              path="/price-concerns"
              element={
                isPromotionsOnly ? (
                  <Navigate to="/promotions/manage" replace />
                ) : isSuperuserActor ? (
                  <PriceConcernsPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/releases"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.APP_RELEASE]}>
                  <ReleasesListPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/releases/new"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.APP_RELEASE]}>
                  <ReleaseCreatePage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="/releases/:id"
              element={
                <RequireAdministrationRead subjects={[PERMISSION_SUBJECTS.APP_RELEASE]}>
                  <ReleaseDetailPage />
                </RequireAdministrationRead>
              }
            />
            <Route
              path="*"
              element={isPromotionsOnly ? <Navigate to="/promotions/manage" replace /> : <DashboardPage />}
            />
          </Routes>
          </CustomerSupportScopeGate>
        </Box>
      </Box>
    </Box>
  )
}

export default DashboardLayout
